import type { HttpRequestConfig } from '../lib/utils/httpClient';
import type {
  InitiateMeetingRequestType,
  InitiateMeetingResponseType,
  UpdateZoomLogRequestType,
  UpdateZoomTimestampsRequestType,
  StartCustomCall,
  UpdateConsultStatusType,
  GenericResponseType,
  ZoomSignatureRequestType,
  ZoomSignatureResponseType,
  ZoomMeetingDetailsRequestType,
  ZoomMeetingDetailsResponseType,
  InviteLinkRequestType,
  ResponseData,
  ParticipantInviteLinkResponseData,
  ParticipantInviteLinkRequestType,
} from './zoomTypes';
import { PORTAL_TYPES } from '../constants/portalType';

export type HttpRequestFn = <T>(config: HttpRequestConfig) => Promise<T>;

export interface UnReadMessageCount {
  unreadMessageCount: number;
}

export interface ZoomParticipantDetailsResponse {
  participantJoined: boolean;
  timedOut: boolean;
}

export const createZoomService = (httpRequest: HttpRequestFn) => ({
  initiateMeetingApi: async (
    userId: number,
    portalType: string,
    payload: InitiateMeetingRequestType
  ): Promise<InitiateMeetingResponseType> => {
    const response = await httpRequest<InitiateMeetingResponseType>({
      url: `/integration/zoom/${userId}/meeting-details`,
      method: 'POST',
      data: payload,
      headers: {
        portalType: portalType || PORTAL_TYPES.CLINICAL,
      },
      apiBaseType: 'INTEGRATION',
    });
    return response;
  },

  updateZoomLogApi: async (
    payload: UpdateZoomLogRequestType
  ): Promise<GenericResponseType> => {
    const response = await httpRequest<GenericResponseType>({
      url: `/clinical/zoom/logs`,
      method: 'POST',
      data: payload,
    });
    return response;
  },

  updateZoomTimestampsApi: async (
    payload: UpdateZoomTimestampsRequestType
  ): Promise<GenericResponseType> => {
    const response = await httpRequest<GenericResponseType>({
      url: `/provider/zoom-call-dateTime`,
      method: 'PUT',
      data: payload,
    });
    return response;
  },

  updateCustomCallStartEvents: async (
    userId: number,
    payload: StartCustomCall
  ): Promise<GenericResponseType> => {
    const response = await httpRequest<GenericResponseType>({
      url: `/clinical/consult/start-custom-call`,
      method: 'POST',
      data: payload,
      headers: {
        userId: userId.toString(),
      },
    });
    return response;
  },

  updateConsultStatus: async (
    userId: number,
    payload: UpdateConsultStatusType
  ): Promise<GenericResponseType> => {
    const response = await httpRequest<GenericResponseType>({
      url: `/clinical/consult/end-custom-call`,
      method: 'PUT',
      data: payload,
      headers: {
        userId: userId.toString(),
      },
    });
    return response;
  },

  getZoomSignature: async (
    portalType: string,
    payload: ZoomSignatureRequestType
  ): Promise<ZoomSignatureResponseType> => {
    const response = await httpRequest<ZoomSignatureResponseType>({
      apiBaseType: 'INTEGRATION',
      url: `/integration/zoom/signature`,
      method: 'POST',
      headers: {
        portalType: portalType || PORTAL_TYPES.CLINICAL,
      },
      data: payload,
    });
    return response;
  },

  getZoomMeetingDetails: async (
    payload: ZoomMeetingDetailsRequestType
  ): Promise<ZoomMeetingDetailsResponseType> => {
    const response = await httpRequest<ZoomMeetingDetailsResponseType>({
      apiBaseType: 'INTEGRATION',
      url: `/meetingdetails`,
      method: 'POST',
      data: payload,
    });
    return response;
  },

  getPaticipentJoinLink: async (payload: ParticipantInviteLinkRequestType) => {
    const response = await httpRequest<ParticipantInviteLinkResponseData>({
      apiBaseType: 'INTEGRATION',
      url: `integration/zoom/participantJoinLink`,
      headers: {
        portalType: 'Facility',
      },
      method: 'POST',
      data: payload,
    });
    return response;
  },

  getInterpreterInviteLink: async (payload: InviteLinkRequestType) => {
    const response = await httpRequest<ResponseData>({
      apiBaseType: 'INTEGRATION',
      url: `integration/zoom/inviteLink`,
      method: 'POST',
      data: payload,
    });
    return response;
  },

  getUnReadCountNotification: async (
    userId: number,
    consultId: number,
    portalType: string
  ) => {
    const response = await httpRequest<UnReadMessageCount>({
      apiBaseType: 'INTEGRATION',
      url: `integration/acs/chat/unread-messages/count?consultId=${consultId}`,
      method: 'GET',
      headers: {
        userId: userId.toString(),
        portalType: portalType,
      },
    });
    return response;
  },

  getZoomParticipantJoinedDetails: async (consultId: number) => {
    const userId = localStorage.getItem('userId') ?? '0';

    const response = await httpRequest<ZoomParticipantDetailsResponse>({
      apiBaseType: 'INTEGRATION',
      url: `integration/zoom/consult/${consultId}/call-result`,
      method: 'GET',
      headers: {
        userId: userId,
      },
    });
    return response;
  },
});
